import { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Stack, ButtonGroup, TextField, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useScopeStatement } from '../../../api/hooks/useScopeStatement';
import { useProject } from '../../../api/hooks/useProjects';
import { useUpsertReifegrad } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildScopeStatementReport } from '../reports/scopeStatementReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { ScopeAttachments } from '../../../components/scope/ScopeAttachments';
import { ScopePreview } from '../../../components/scope/ScopePreview';
import { SCOPE_ANHAENGE_AKTIV } from '../../../components/scope/scopeFlags';

const TABLE_BOILERPLATE = `\n| Spalte 1 | Spalte 2 | Spalte 3 |\n| --- | --- | --- |\n| Zelle | Zelle | Zelle |\n`;

export function ScopeStatementTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { data, isLoading, save } = useScopeStatement(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [value, setValue] = useState<string>('');
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const initialized = useRef(false);
  const [preview, setPreview] = useState(false);

  const autosave = useAutosave<string>({
    onSave: async (v) => {
      await save(v);
      // Reifegrad-Tracking Schritt 9: content-length-basiert.
      const len = v.trim().length;
      const level = len === 0 ? 0 : len < 200 ? 1 : len < 600 ? 3 : len < 1500 ? 4 : 5;
      try { await upsertReifegrad.mutateAsync({ schritt: 9, data: { reifegrad: level } }); } catch { /* best-effort */ }
    },
  });

  useEffect(() => {
    // Server-Daten beim ersten Laden einmal spiegeln; Refetches während des
    // Tippens dürfen den Editor-State NICHT überschreiben (initialized-Guard).
    if (!initialized.current && data) {
      setValue(data.scope_statement ?? '');
      initialized.current = true;
    }
  }, [data]);

  const applyValue = (next: string) => {
    setValue(next);
    autosave.schedule(next);
  };

  const insertAtCursor = (pre: string, post = '') => {
    const ta = taRef.current;
    if (!ta) {
      applyValue(value + pre + post);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + pre + selected + post + value.slice(end);
    applyValue(next);
    requestAnimationFrame(() => {
      ta.focus();
      const caret = start + pre.length + selected.length + post.length;
      ta.setSelectionRange(caret, caret);
    });
  };

  // Strg+S erzwingt sofortiges Speichern (statt auf den Debounce zu warten).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      autosave.flush();
    }
  };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('scope.title', 'Scope Statement') };
  const buildReport = () =>
    buildScopeStatementReport({ scopeStatement: data?.scope_statement ?? '' });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box onKeyDown={onKeyDown}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">{t('scope.title', 'Scope Statement')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('scope.subtitle', 'Projektumfang in Markdown beschreiben (Ziele, In-Scope, Out-of-Scope, Annahmen, Abnahmekriterien).')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PdfExportButton meta={pdfMeta} build={buildReport} />
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
        </Stack>
      </Stack>

      <PmCard>
        <ButtonGroup size="small" sx={{ mb: 1.5, flexWrap: 'wrap' }} aria-label={t('scope.toolbar', 'Markdown-Toolbar')}>
          <Button onClick={() => insertAtCursor('## ')} aria-label="H2">H2</Button>
          <Button onClick={() => insertAtCursor('### ')} aria-label="H3">H3</Button>
          <Button onClick={() => insertAtCursor('**', '**')} aria-label={t('scope.bold', 'Fett')}><strong>B</strong></Button>
          <Button onClick={() => insertAtCursor('*', '*')} aria-label={t('scope.italic', 'Kursiv')}><em>I</em></Button>
          <Button onClick={() => insertAtCursor('- ')} aria-label={t('scope.bulletList', 'Liste')}>{t('scope.bulletList', '• Liste')}</Button>
          <Button onClick={() => insertAtCursor('1. ')} aria-label={t('scope.numberList', 'Nummerierte Liste')}>{t('scope.numberList', '1. Nummer')}</Button>
          <Button onClick={() => insertAtCursor(TABLE_BOILERPLATE)} aria-label={t('scope.table', 'Tabelle')}>{t('scope.table', '⊞ Tabelle')}</Button>
          <Button onClick={() => setPreview((p) => !p)} aria-pressed={preview}>
            {preview ? t('scope.edit', 'Bearbeiten') : t('scope.preview', 'Vorschau')}
          </Button>
        </ButtonGroup>

        {preview ? <ScopePreview projectId={projectId} value={value} /> : <TextField
          inputRef={taRef}
          multiline
          rows={22}
          fullWidth
          value={value}
          onChange={(e) => applyValue(e.target.value)}
          placeholder={t('scope.placeholder', '## Zielsetzung\n\n## In-Scope\n\n## Out-of-Scope\n\n## Annahmen\n\n## Abnahmekriterien')}
          slotProps={{ htmlInput: { 'aria-label': t('scope.editor', 'Scope-Statement-Editor') } }}
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 14 } }}
        />}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('scope.hint', 'Markdown wird unterstützt. Änderungen werden automatisch gespeichert (Strg+S speichert sofort).')}
        </Typography>

        {SCOPE_ANHAENGE_AKTIV && (
          <ScopeAttachments projectId={projectId} onInsert={(md) => { setPreview(false); insertAtCursor(`\n${md}\n`); }} />
        )}
      </PmCard>
    </Box>
  );
}
